import React, { useMemo, useCallback } from 'react'
import Flatpickr from 'react-flatpickr'

import flatpickr from 'flatpickr'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import moment from 'moment/moment'
import PropTypes from 'prop-types'

import first from 'lodash/first'
import isBoolean from 'lodash/isBoolean'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import omitBy from 'lodash/omitBy'

import {
  momentBackDateFormat,
  momentBackDateTimeFormat,
  momentBackTimeFormat
} from '@galo-monitor/utils/dates'

import useStyles from './styles'

if (typeof window !== 'undefined') {
  flatpickr.localize(Portuguese)
}

const CalendarPicker = (props) => {
  const {
    mode,
    minDate,
    maxDate,
    minTime,
    maxTime,
    disabledDates,
    options: externalOptions,
    onChange,
    children,
    value
  } = props

  useStyles()

  const relative = useMemo(() => !children, [children])

  const momentBack = useMemo(
    () => {
      switch (mode) {
        case 'time': return momentBackTimeFormat
        case 'datetime': return momentBackDateTimeFormat
        case 'date':
        default: return momentBackDateFormat
      }
    },
    [mode]
  )

  const dateFormat = useMemo(
    () => {
      switch (mode) {
        case 'time': return 'H:i'
        case 'datetime': return 'Y-m-d H:i'
        case 'date':
        default: return 'Y-m-d'
      }
    },
    [mode]
  )

  const date = useMemo(
    () => {
      if (isEmpty(value)) {
        return null
      }
      const test = moment(value, momentBack, true)
      if (test.isValid()) {
        return test.format(momentBack)
      }
      return value
    },
    [momentBack, value]
  )

  const options = useMemo(
    () => omitBy({
      ...externalOptions,
      disableMobile: true,
      inline: relative,
      static: relative,
      wrap: !relative,
      altInputClass: relative ? 'hide-flatpickr-native-input' : '',
      appendTo: relative ? undefined : document.getElementById('calendar-picker-portal'),
      dateFormat,
      minDate,
      maxDate,
      minTime,
      maxTime,
      disable: disabledDates,
      enableTime: mode === 'time' || mode === 'datetime',
      noCalendar: mode === 'time',
      allowInput: true,
      clickOpens: !relative
    }, item => !isBoolean(item) && isEmpty(item)),
    [mode, relative, externalOptions, disabledDates, minDate, maxDate, minTime, maxTime, dateFormat]
  )

  const handleChange = useCallback(
    ([...dates]) => {
      let values = map(dates, newDate => moment(newDate).format(momentBack))
      if (mode === 'date') {
        values = first(values)
      }
      onChange(values)
    },
    [mode, onChange, momentBack]
  )

  return (
    <Flatpickr
      value={ date }
      options={ options }
      onChange={ handleChange }
    >
      <div className="flatpickr">
        {children}
      </div>
    </Flatpickr>
  )
}

CalendarPicker.propTypes = {
  mode: PropTypes.oneOf(['date', 'datetime', 'time']),
  minDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string
  ]),
  maxDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string
  ]),
  minTime: PropTypes.string,
  maxTime: PropTypes.string,
  disabledDates: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  options: PropTypes.object,
  children: PropTypes.any,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
}

CalendarPicker.defaultProps = {
  mode: 'date',
  minDate: null,
  maxDate: null,
  minTime: null,
  maxTime: null,
  disabledDates: [],
  onChange: () => {},
  options: {},
  children: undefined,
  value: null
}

export default CalendarPicker
